import axios from 'axios';

import { ApiResponse, PagedResponse } from '../api-helpers';
import { resolveLookupDefaults } from './customerPropertiesDefaults';
import vendorApi from './vendorApi';
import axiosInstance from '../config/axios';
import { API_BASE_URL } from '../config/constants';
// import { Customer } from '../types/customer'; // Kullanılmıyor
import { CurrencyResponse } from '../hooks/useCurrencies'; 
import { TaxOfficeResponse } from '../hooks/useTaxOffices';
import { AddressTypeResponse, AddressResponse } from '../types/address';

// FE loglarını sadece development'ta veya açıkça enable edildiğinde göstermek için yardımcılar
const __DEV_LOG = (() => {
  try {
    const isDev = typeof process !== 'undefined' && (process as any).env?.NODE_ENV === 'development';
    const flag = typeof window !== 'undefined' && (window as any).localStorage?.getItem('debugAllLogs') === 'true';
    return isDev || flag;
  } catch {
    return false;
  }
})();
// createCustomer akışı için özel debug flag'i
const __debugCreate = (() => {
  try {
    const flag = typeof window !== 'undefined' && (window as any).localStorage?.getItem('debugCustomerCreate') === 'true';
    return __DEV_LOG || flag;
  } catch {
    return __DEV_LOG;
  }
})();
const devLog = (...args: any[]) => { if (__DEV_LOG || __debugCreate) console.log(...args); };
const devWarn = (...args: any[]) => { if (__DEV_LOG || __debugCreate) console.warn(...args); };
const devGroupCollapsed = (...args: any[]) => { if ((
  __DEV_LOG || __debugCreate) && typeof console.groupCollapsed === 'function') console.groupCollapsed(...args as any); };
const devGroupEnd = () => { if ((__n=>__n)(__DEV_LOG || __debugCreate) && typeof console.groupEnd === 'function') console.groupEnd(); };

// API hata yönetimi için yardımcı fonksiyon
const handleApiError = (error: any, errorPrefix: string = 'İşlem'): ApiResponse<any> => {
  if (error.response) {
    return {
      success: false,
      message: `${errorPrefix} sırasında hata oluştu: ${error.response.status} ${error.response.statusText}`,
      data: null
    };
  } else if (error.request) {
    return {
      success: false,
      message: 'Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.',
      data: null
    };
  } else {
    return {
      success: false,
      message: `${errorPrefix} sırasında hata oluştu: ${error.message || 'Bilinmeyen bir hata'}`,
      data: null
    };
  }
};

// Geçici olarak any kullanıyoruz, bu tiplerin types/customer.ts içinde doğru şekilde tanımlanması gerekir.
export type CustomerDetailResponse = any;
export type CustomerFilterRequest = any;
export type CustomerUpdateRequest = any;
export type CustomerCreateRequestNew = any;
export type CustomerUpdateResponseNew = any;
export type CustomerCreateResponseNew = any;
export type CustomerListResponse = any;
export type CustomerResponse = any;

// Çoklu DB mükerrer kontrol sonucu
export interface MultiDbDupResult {
  SourceDb: string;
  CustomerCode: string;
  CustomerName: string;
  TaxNumber?: string | null;
  IdentityNum?: string | null;
}

// Token yanıt tipi
export interface TokenResponse {
  token: string;
}

// Sevkiyat yöntemi yanıt tipi
export interface ShipmentMethodResponse {
  shipmentMethodCode: string;
  shipmentMethodName: string;
  isBlocked: boolean;
  isDefault?: boolean;
}

interface CustomerData {
  CustomerCode: any;
  CustomerName: any;
  CustomerSurname: any;
  CustomerTypeCode: number;
  CompanyCode: any;
  CountryCode: any;
  StateCode: any;
  CityCode: any;
  DistrictCode: any;
  Address: any;
  ContactName: any;
  OfficeCode: any;
  ExchangeTypeCode: any;
  isIndividualAcc: any;
  CreatedUserName: any;
  LastUpdatedUserName: any;
  TaxNumber?: string;
  TaxOffice?: string;
  TaxOfficeCode?: string;
  IdentityNumber?: string;
  IdentityNum?: string;
  CustomerIdentityNumber?: string;
  DueDateFormulaCode?: any;
  IsSubjectToEInvoice?: boolean;
  IsSubjectToEDispatch?: boolean;
  EInvoiceStartDate?: string;
  EShipmentStartDate?: string;
  CurrencyCode?: string;
}

// API metodlarını içeren temel nesne
export const api = {
  get: axiosInstance.get.bind(axiosInstance),
  post: axiosInstance.post.bind(axiosInstance),
  put: axiosInstance.put.bind(axiosInstance),
  delete: axiosInstance.delete.bind(axiosInstance),
  patch: axiosInstance.patch.bind(axiosInstance)
};

// Diğer API çağrılarınızın olduğu varsayılan bir yapı

// Location API fonksiyonları
export const locationApi = {
  // Token ile ülke listesini getiren fonksiyon
  getCountriesWithToken: async (token: string, langCode: string = 'TR'): Promise<any[]> => {
    devLog('API: Fetching countries with token and langCode:', langCode);
    try {
      const response = await axios.get<ApiResponse<any[]>>(`${API_BASE_URL}/api/v1/Location/countries-with-token`, {
        params: { token, langCode }
      });
      
      if (response.data.success && response.data.data) {
        devLog(`API: Successfully fetched ${response.data.data.length} countries with token`);
        return response.data.data;
      } else {
        devWarn('API: Countries endpoint with token returned success=false or no data', response.data);
        return [];
      }
    } catch (error) {
      console.error('API: Error fetching countries with token', error);
      return [];
    }
  },
  // Token ile bölge listesini getiren fonksiyon
  getStatesWithToken: async (token: string, langCode: string = 'TR'): Promise<any[]> => {
    devLog('API: Fetching states with token and langCode:', langCode);
    try {
      const response = await axios.get<ApiResponse<any[]>>(`${API_BASE_URL}/api/v1/Location/states-with-token`, {
        params: { token, langCode }
      });
      
      if (response.data.success && response.data.data) {
        devLog(`API: Successfully fetched ${response.data.data.length} states with token`);
        return response.data.data;
      } else {
        devWarn('API: States endpoint with token returned success=false or no data', response.data);
        return [];
      }
    } catch (error) {
      console.error('API: Error fetching states with token', error);
      return [];
    }
  },
  
  // Token ile hiyerarşik lokasyon verisini getiren fonksiyon
  getHierarchyWithToken: async (token: string, langCode: string = 'TR', countryCode: string = 'TR'): Promise<any> => {
    devLog('API: Fetching location hierarchy with token, langCode:', langCode, 'countryCode:', countryCode);
    try {
      const response = await axios.get<ApiResponse<any>>(`${API_BASE_URL}/api/v1/Location/hierarchy-with-token`, {
        params: { token, langCode, countryCode }
      });
      
      if (response.data.success && response.data.data) {
        devLog('API: Successfully fetched location hierarchy with token');
        return response.data.data;
      } else {
        devWarn('API: Hierarchy endpoint with token returned success=false or no data', response.data);
        return null;
      }
    } catch (error) {
      console.error('API: Error fetching location hierarchy with token', error);
      return null;
    }
  }
};

export const customerApi = {
  // Normal müşteri kaydı sonrası token alma fonksiyonu
  getCustomerToken: async (customerCode: string): Promise<string | undefined> => {
    devLog('API: Müşteri için token alınıyor:', customerCode);
    try {
      // Mevcut token'ları kontrol et
      const adminToken = localStorage.getItem('token'); // Admin token'ı
      const userToken = localStorage.getItem('accessToken'); // Normal kullanıcı token'ı
      
      // Kullanılacak token'ı belirle
      const authToken = adminToken || userToken;
      
      if (!authToken) {
        console.error('API: Token bulunamadı! Ne admin ne de kullanıcı tokeni mevcut değil');
        return undefined;
      }
      
      devLog('API: Token isteği için kullanılan token tipi:', adminToken ? 'admin' : 'normal kullanıcı');
      
      // API'nin beklediği format: CustomerTokenRequest
      const response = await axios.post<ApiResponse<TokenResponse>>(
        `${API_BASE_URL}/api/v1/Customer/get-token`,
        { CustomerCode: customerCode }, // CustomerCode büyük harfle başlıyor
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      if (response.data.success && response.data.data && response.data.data.token) {
        devLog('API: Müşteri için token başarıyla alındı:', response.data.data.token.substring(0, 15) + '...');
        return response.data.data.token;
      } else {
        devWarn('API: Müşteri token endpoint başarısız oldu veya token dönmedi', response.data);
        return undefined;
      }
    } catch (error) {
      console.error('API: Müşteri token alınırken hata oluştu', error);
      return undefined;
    }
  },
  getCustomers: async (filter: CustomerFilterRequest): Promise<ApiResponse<PagedResponse<CustomerListResponse>>> => {
    devLog('API: Fetching customers with filter:', filter);
    
    // Kullanıcı bilgisini localStorage'dan al
    const userJson = localStorage.getItem('user');
    let userId = null;
    let userRoles: string[] = [];
    
    if (userJson) {
      try {
        const userData = JSON.parse(userJson);
        userId = userData.id;
        userRoles = userData.roles || [];
        devLog('API: User roles from localStorage:', userRoles);
      } catch (e) {
        console.error('API: Error parsing user data from localStorage', e);
      }
    }
    
    // Eğer kullanıcı satış elemanı ise ve admin değilse, sadece kendi müşterilerini göster
    if (userId && userRoles.includes('sales') && !userRoles.includes('admin')) {
      devLog('API: Sales user detected, filtering customers by sales person');
      // Satış elemanı filtresi ekle
      filter.salesPersonId = userId;
    }
    
    const response = await axiosInstance.get('/api/v1/CustomerBasic/customers', { params: filter });
    devLog('API: Customers response:', response.data);
    return response.data;
  },
  getVendors: async (filter: CustomerFilterRequest): Promise<ApiResponse<PagedResponse<CustomerListResponse>>> => {
    devLog('API: Fetching vendors with filter:', filter);
    const response = await axiosInstance.get('/api/v1/CustomerBasic/vendors', { params: filter });
    devLog('API: Vendors response:', response.data);
    return response.data;
  },
  getCustomerByCode: async (customerCode: string): Promise<ApiResponse<CustomerDetailResponse>> => {
    devLog('API: Fetching customer by code:', customerCode);
    const response = await axiosInstance.get<ApiResponse<CustomerDetailResponse>>(`/api/v1/CustomerBasic/${customerCode}`);
    devLog('API: Customer detail response:', response.data);
    return response.data;
  },
  // Çoklu veritabanında (STD, STL, SCT, SCL) TCKN/VKN ile mükerrer kontrol
  multiDbDupCheck: async (params: { taxNumber?: string; identityNum?: string }): Promise<ApiResponse<MultiDbDupResult[]>> => {
    const { taxNumber, identityNum } = params || {};
    devLog('API: Multi-DB dup check çağrısı', { taxNumber, identityNum });
    const response = await axiosInstance.get<ApiResponse<MultiDbDupResult[]>>(
      '/api/v1/CustomerBasic/multi-db-dup-check',
      { params: { taxNumber: taxNumber || undefined, identityNum: identityNum || undefined } }
    );
    return response.data;
  },
  // Son 10 benzersiz müşteri kodu ve veritabanı varlık bayrakları
  getLatestCustomersPerCode: async (): Promise<any[]> => {
    try {
      const res = await axiosInstance.get(`/api/v1/customers/latest-per-code`);
      return Array.isArray(res.data) ? res.data : res.data?.data || [];
    } catch (e) {
      console.error('API: latest-per-code alınırken hata', e);
      return [];
    }
  },
  // createCustomer fonksiyonu kaldırıldı, sadece createCustomerBasic fonksiyonu kullanılıyor
  createCustomerBasic: async (customerData: any, options?: { requestId?: string }): Promise<ApiResponse<CustomerCreateResponseNew>> => {
  try {
    // Veritabanına göre lookup defaultlarını al (formdan gelmezse fallback olarak kullanılacak)
    const { priceGroupCode: preferredPriceCode } = resolveLookupDefaults();

    // Frontend'den gelen verileri backend'in beklediği formata dönüştür
    // Müşteri kodunu boş gönder, backend otomatik oluşturacak
    // Vergi dairesi alanı farklı şekillerde gelebilir: string kod, "taxOfficeCode", veya nesne { code | taxOfficeCode }
    const taxOfficeCodeNormalized = (
      customerData.taxOfficeCode ||
      customerData.TaxOfficeCode ||
      (customerData.taxOffice && (customerData.taxOffice.code || customerData.taxOffice.taxOfficeCode)) ||
      customerData.taxOffice ||
      ''
    );

    // Boolean normalizasyonu (checkbox/text)
    const toBool = (v: any): boolean => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v === 1;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'on' || s === 'yes' || s === 'evet';
      }
      return false;
    };

    // Kullanıcının seçtiği fiyat/iskonto gruplarını birden çok olası alandan oku
    const resolvedWholesalePriceGroupCode = (
      customerData.wholesalePriceGroupCode ||
      customerData.WholesalePriceGroupCode ||
      customerData.priceGroupCode ||
      customerData.PriceGroupCode ||
      customerData.selectedPriceGroup ||
      (customerData.lookup && (customerData.lookup.priceGroupCode || customerData.lookup.selectedPriceGroup)) ||
      preferredPriceCode ||
      ''
    );

    // İskonto grubu için: Kullanıcı özellikle boş ('') bıraktıysa bunu saygıyla koru ve default'a düşme.
    // Sadece alan hiç sağlanmamışsa resolveLookupDefaults() fallback'ını kullan.
    const hasOwn = (obj: any, key: string) => Object.prototype.hasOwnProperty.call(obj, key);
    // İskonto grubu: Kullanıcı boş bıraktıysa kesinlikle boş gönder. Default'a düşme.
    const resolvedDiscountGroupCode = (
      // Düz alanlar
      (hasOwn(customerData, 'discountGroupCode') ? customerData.discountGroupCode : undefined) ??
      (hasOwn(customerData, 'DiscountGroupCode') ? customerData.DiscountGroupCode : undefined) ??
      (hasOwn(customerData, 'selectedDiscountGroup') ? customerData.selectedDiscountGroup : undefined) ??
      // lookup içinde olabilir
      (customerData.lookup && (hasOwn(customerData.lookup, 'discountGroupCode') ? customerData.lookup.discountGroupCode : undefined)) ??
      (customerData.lookup && (hasOwn(customerData.lookup, 'selectedDiscountGroup') ? customerData.lookup.selectedDiscountGroup : undefined)) ??
      // Hiçbiri sağlanmamışsa, NULL gönder
      null
    );

    // E-İrsaliye (EDispatch) bayrağı ve tarihi
    const resolvedIsEDispatch = toBool(
      customerData.isSubjectToEDispatch ?? customerData.isSubjectToEShipment ?? customerData.EDispatch ?? customerData.EShipment
    );

    const resolvedEDispatchStartDate = (
      customerData.eDispatchStartDate ||
      customerData.EDispatchStartDate ||
      customerData.eShipmentStartDate ||
      customerData.EShipmentStartDate ||
      (resolvedIsEDispatch ? new Date().toISOString() : undefined)
    );
    const resolvedEDispatchStartDateIso = resolvedEDispatchStartDate
      ? new Date(resolvedEDispatchStartDate).toISOString()
      : '1900-01-01T00:00:00.000Z';
    // E-Fatura başlangıç tarihi (ISO normalize)
    const resolvedEInvoiceStartDate = (
      customerData.eInvoiceStartDate ||
      customerData.EInvoiceStartDate ||
      (toBool(customerData.isSubjectToEInvoice) ? new Date().toISOString() : undefined)
    );
    const resolvedEInvoiceStartDateIso = resolvedEInvoiceStartDate
      ? new Date(resolvedEInvoiceStartDate).toISOString()
      : '1900-01-01T00:00:00.000Z';
  const formattedData: CustomerCreateRequestNew = {
      CustomerCode: '',
      CustomerName: customerData.customerName || '',
      CustomerSurname: customerData.customerSurname || '',
      CustomerTypeCode: Number(customerData.customerTypeCode) || 3,
      CompanyCode: Number(customerData.companyCode) || 1,
      OfficeCode: customerData.officeCode || 'M',
      CurrencyCode: customerData.currencyCode || 'TRY',
      IsIndividualAcc: customerData.isIndividualAcc || false,
      CreatedUserName: customerData.createdUserName || 'system',
      LastUpdatedUserName: customerData.lastUpdatedUserName || 'system',
      TaxNumber: customerData.taxNumber || '',
      IdentityNum: customerData.identityNum || customerData.identityNumber || customerData.IdentityNum || '',
      TaxOfficeCode: taxOfficeCodeNormalized,
      IsSubjectToEInvoice: customerData.isSubjectToEInvoice || false,
      IsSubjectToEDispatch: resolvedIsEDispatch,
      // Bazı backendler EShipment alan adını bekliyor olabilir: ikisini de gönderelim
      IsSubjectToEShipment: resolvedIsEDispatch,
      EInvoiceStartDate: resolvedEInvoiceStartDateIso,
      EDispatchStartDate: resolvedEDispatchStartDateIso,
      EShipmentStartDate: resolvedEDispatchStartDateIso,
      CityCode: customerData.cityCode || '',
      DistrictCode: customerData.districtCode || '',
      // Yeni: lookup seçimleri (kullanıcının seçtiği > DB default > boş)
      WholesalePriceGroupCode: String(resolvedWholesalePriceGroupCode || '')
    };

    // Debug loglarını sadece development ortamında veya explicit flag ile göster
    const __debugCreate = (() => {
      const isDev = typeof process !== 'undefined' && (process as any).env?.NODE_ENV === 'development';
      let flag = false;
      try {
        flag = typeof window !== 'undefined' && !!(window as any).localStorage &&
          (window as any).localStorage.getItem('debugCustomerCreate') === 'true';
      } catch (_) {}
      return isDev || flag;
    })();

    // Debug: kritik alanları logla (sadece dev/flag)
    if (__debugCreate) {
      devLog('Resolve Check - Price/Discount:', {
        resolvedWholesalePriceGroupCode,
        resolvedDiscountGroupCode,
        preferredPriceCode
      });
      devLog('Resolve Check - EDispatch:', {
        isSubjectToEDispatch_input: customerData.isSubjectToEDispatch,
        isSubjectToEShipment_input: customerData.isSubjectToEShipment,
        resolvedIsEDispatch,
        resolvedEDispatchStartDate
      });
      devLog('Müşteri oluşturma için gönderilecek veriler:', formattedData);
    }
    
    // Gönderim: Çoklu-DB akışını tetikleyen JSON endpoint'i kullan
    // Not: Backend'de multi-DB için [HttpPost("Create")] route'u mevcut
    // Tarih alanları gerekiyorsa JSON gövdesine ekliyoruz
    const payload = {
      ...formattedData,
      // Güvenli tarafta kalmak adına, null yerine boş string kullanımı korunuyor
      IdentityNum: formattedData.IdentityNum ?? ''
    } as CustomerCreateRequestNew;

    if (__debugCreate) {
      devLog('JSON payload (multi-DB Create):', payload);
    }
    // Target DB'leri normalize et ve backend'in beklediği alan adına (TargetDatabases) eşle
    const allowedTargets = ['V3_StDeri', 'V3_StDeriLTD', 'V3_SerkanCanta', 'V3_SerkanCantaLTD'];
    let incomingTargets: any = (customerData as any).targetDatabases ?? (customerData as any).TargetDatabases ?? [];
    if (typeof incomingTargets === 'string') incomingTargets = [incomingTargets];
    const normalizedTargets = (Array.isArray(incomingTargets) ? incomingTargets : [])
      .map((x) => String(x))
      .filter((x) => allowedTargets.includes(x));
    // Tekilleştir, sırayı koru
    const uniqueTargets: string[] = [];
    for (const t of normalizedTargets) {
      if (!uniqueTargets.includes(t)) uniqueTargets.push(t);
    }
    // Master'ı dinamik belirle ve sıralamayı gruplandır
    let orderedTargets: string[] = [];
    if (uniqueTargets.includes('V3_StDeri')) {
      // STD master senaryosu
      const pref = ['V3_StDeri', 'V3_StDeriLTD', 'V3_SerkanCanta', 'V3_SerkanCantaLTD'];
      orderedTargets = pref.filter((k) => uniqueTargets.includes(k));
    } else if (uniqueTargets.includes('V3_SerkanCanta')) {
      // SCT master fallback senaryosu
      const pref = ['V3_SerkanCanta', 'V3_SerkanCantaLTD', 'V3_StDeri', 'V3_StDeriLTD'];
      orderedTargets = pref.filter((k) => uniqueTargets.includes(k));
    } else {
      // Ekstrem durumda gelen sırayı koru
      orderedTargets = [...uniqueTargets];
    }
    if (__debugCreate) {
      devLog('Normalize TargetDatabases (ordered):', orderedTargets);
    }

    // İstek korelasyonu için RequestId üret (dışarıdan verilmediyse)
    const __reqId = options?.requestId ?? `CC-${Date.now()}`;

    try {
      // Konsola özet log (tek blok/atomic gönderim) - sadece dev/flag
      if (__debugCreate && typeof window !== 'undefined' && (window as any).console) {
        // payload objesi POST sırasında oluşturuluyor; özet değerleri burada hesaplıyoruz
        const addrCount = Array.isArray(customerData?.Addresses) ? customerData.Addresses.length : 0;
        const commCount = Array.isArray(customerData?.Communications) ? customerData.Communications.length : 0;
        const contactCount = Array.isArray(customerData?.Contacts) ? customerData.Contacts.length : 0;
        const propCount = customerData?.CustomerProperties ? Object.keys(customerData.CustomerProperties).length : 0;
        const perDbPropKeys = customerData?.PerDbCustomerProperties ? Object.keys(customerData.PerDbCustomerProperties) : [];
        // group log
        devGroupCollapsed(`%c[FE][Customer/Create] reqId=${__reqId}`,'color:#0b73ec');
        devLog('CustomerCode/Name/Type:', customerData?.CustomerCode, customerData?.CustomerName, customerData?.CustomerTypeCode);
        devLog('Company/Office/Currency:', customerData?.CompanyCode, customerData?.OfficeCode, customerData?.CurrencyCode);
        devLog('Counts | Addresses=%d, Communications=%d, Contacts=%d, CustomerProperties=%d', addrCount, commCount, contactCount, propCount);
        if (addrCount > 0) devLog('Addresses[0..]:', customerData.Addresses);
        if (commCount > 0) devLog('Communications[0..]:', customerData.Communications);
        if (contactCount > 0) devLog('Contacts[0..]:', customerData.Contacts);
        if (propCount > 0) devLog('CustomerProperties:', customerData.CustomerProperties);
        if (perDbPropKeys.length > 0) devLog('PerDbCustomerProperties(dbs):', perDbPropKeys);
        if (Array.isArray(customerData?.TargetDatabases)) devLog('TargetDatabases:', customerData.TargetDatabases);
      }
    } catch (_) {
      // console log hatalarını yut
    }

    const response = await axiosInstance.post<ApiResponse<CustomerCreateResponseNew>>(
      '/api/v1/Customer/Create',
      {
        ...payload,
        // DiscountGroupCode: Kullanıcı seçmediyse hiç gönderme
        ...(resolvedDiscountGroupCode && String(resolvedDiscountGroupCode).trim() !== ''
          ? { DiscountGroupCode: String(resolvedDiscountGroupCode).trim() }
          : {}),
        // Atomic: nested koleksiyonları isteğe dahil et
        ...(Array.isArray(customerData.Addresses) && customerData.Addresses.length > 0
          ? {
              Addresses: customerData.Addresses.map((a: any) => ({
                ...a,
                // Backend AddressID string bekliyor
                AddressID: a?.AddressID !== undefined && a?.AddressID !== null ? String(a.AddressID) : '0'
              }))
            }
          : {}),
        ...(Array.isArray(customerData.Communications) && customerData.Communications.length > 0
          ? { Communications: customerData.Communications }
          : {}),
        ...(Array.isArray(customerData.Contacts) && customerData.Contacts.length > 0
          ? { Contacts: customerData.Contacts }
          : {}),
        // Alt Müşteriler (prSubCurrAcc)
        ...(Array.isArray((customerData as any).SubCustomers) && (customerData as any).SubCustomers.length > 0
          ? { SubCustomers: (customerData as any).SubCustomers }
          : {}),
        // Multi-DB genişletmeleri: FE'den gelirse aynen ilet
        ...(customerData?.SalespersonCode ? { SalespersonCode: customerData.SalespersonCode } : {}),
        ...(customerData?.PerDbSalespersonCodes ? { PerDbSalespersonCodes: customerData.PerDbSalespersonCodes } : {}),
        // Müşteri özellikleri (genel + per-DB)
        ...(customerData?.CustomerProperties && Object.keys(customerData.CustomerProperties).length > 0
          ? {
              CustomerProperties: Object.keys(customerData.CustomerProperties).reduce((acc: any, k: string) => {
                // Anahtarlar JSON'da string olacağından, sayısal görünümlüyse normalize et
                const nk = String(Number(k));
                acc[nk] = String(customerData.CustomerProperties[k]);
                return acc;
              }, {})
            }
          : {}),
        ...(customerData?.PerDbCustomerProperties ? { PerDbCustomerProperties: customerData.PerDbCustomerProperties } : {}),
        // Yeni: fiyat/iskonto per-DB override sözlükleri
        ...(customerData?.PerDbWholesalePriceGroupCode ? { PerDbWholesalePriceGroupCode: customerData.PerDbWholesalePriceGroupCode } : {}),
        ...(customerData?.PerDbDiscountGroupCode ? { PerDbDiscountGroupCode: customerData.PerDbDiscountGroupCode } : {}),
        // Hedef veritabanları - backend büyük T ile bekliyor
        ...(orderedTargets.length > 0 ? { TargetDatabases: orderedTargets } : {})
      } as CustomerCreateRequestNew,
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': __reqId
        }
      }
    );
    try {
      if (__debugCreate && typeof window !== 'undefined' && (window as any).console) {
        devLog('%c[FE][Customer/Create] response reqId=%s','color:#0b73ec', __reqId, response?.data);
        devGroupEnd();
      }
    } catch (_) {}
    return response.data;
  } catch (error: unknown) {
    console.error('API: Customer create error:', error);
    
    // Axios hata detaylarını logla
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Sunucu yanıtı ile dönen hata (4xx, 5xx)
        console.error('API error response details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
        
        // Hata mesajını detaylı göster
        if (error.response.data) {
          console.error('API error response data:', JSON.stringify(error.response.data, null, 2));
        }
        
        // ModelState hatalarını göster
        if (error.response.data && 'errors' in error.response.data) {
          console.error('API validation errors:', (error.response.data as any).errors);
        }
        
        // Hata mesajını döndür
        if (error.response.data && 'message' in error.response.data) {
          throw new Error(`API Error: ${(error.response.data as any).message}`);
        }
      } else if (error.request) {
        // İstek yapıldı ancak yanıt alınamadı
        console.error('No response received:', error.request);
      }
    }
    
    // Hata fırlat
    throw error;
  }
  },
  updateCustomer: async (customerData: CustomerUpdateRequest): Promise<ApiResponse<CustomerUpdateResponseNew>> => {
    devLog('API: Updating customer:', customerData.customerCode, customerData);
    const response = await axiosInstance.post<ApiResponse<CustomerUpdateResponseNew>>(`/api/v1/Customer/update`, customerData);
    devLog('API: Update customer response:', response.data);
    return response.data;
  },
  getCustomerAddresses: async (customerCode: string): Promise<AddressResponse[]> => {
    devLog('API: Fetching addresses for customer:', customerCode);
    const response = await axiosInstance.get<ApiResponse<AddressResponse[]>>(`/api/v1/CustomerAddress/${customerCode}/addresses`);
    if (response.data.success) {
      devLog('API: Customer addresses fetched successfully', response.data.data);
      return response.data.data;
    }
    console.error('API: Error fetching customer addresses', response.data.message);
    throw new Error(response.data.message || 'Müşteri adresleri alınamadı');
  },
  getCustomerAddressById: async (customerCode: string, addressId: string): Promise<AddressResponse> => {
    // Bu endpoint backend'de olmayabilir, kontrol edin.
    const response = await axiosInstance.get<ApiResponse<AddressResponse>>(`/api/v1/CustomerAddress/${customerCode}/addresses/${addressId}`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Adres detayı alınamadı');
  },
  createCustomerAddress: async (customerCode: string, addressData: any, token?: string, options?: { requestId?: string }): Promise<ApiResponse<any>> => {
    // Varsayılan olarak isDefault true olarak ayarla
    const data = {
      ...addressData,
      isDefault: addressData.isDefault !== undefined ? addressData.isDefault : true
    };
    
    try {
      devLog('Gönderilen adres verisi:', data);
      
      // Normal müşteri oluşturma akışında token kullanma, axiosInstance kullan
      // QR code akışında ise token kullanılacak
      if (token) {
        // Token ile QR code akışı - doğrudan axios kullan
        devLog('QR Code akışı: Adres eklemede özel token kullanılıyor');
        
        const headers: any = { 'Content-Type': 'application/json' };
        // Token'ın başında 'Bearer ' var mı kontrol et
        const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        headers['Authorization'] = tokenValue;
        if (options?.requestId) {
          headers['X-Request-Id'] = options.requestId;
        }
        devLog('Adres API çağrısına eklenen Authorization header:', tokenValue.substring(0, 20) + '...');
        
        // axiosInstance yerine doğrudan axios kullan
        const response = await axios.post<ApiResponse<any>>(
          `${API_BASE_URL}/api/v1/CustomerAddress/${customerCode}/addresses`, 
          data,
          { headers }
        );
        return response.data;
      } else {
        // Normal akış - axiosInstance kullan (admin token'ı ile)
        devLog('Normal akış: Adres eklemede admin token kullanılıyor');
        const response = await axiosInstance.post<ApiResponse<any>>(
          `/api/v1/CustomerAddress/${customerCode}/addresses`, 
          data,
          options?.requestId ? { headers: { 'X-Request-Id': options.requestId } } : undefined
        );
        return response.data;
      }
    } catch (error: any) {
      console.error('Adres eklerken hata detayı:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        requestData: data
      });
      
      // Hata mesajını daha detaylı göster
      if (error.response?.data) {
        console.error('Backend hata mesajı:', JSON.stringify(error.response.data, null, 2));
      }
      // Hata durumunu işle ve uygun yanıtı döndür
      if (error.response) {
        return {
          success: false,
          message: `İletişim eklenirken hata oluştu: ${error.response.status} ${error.response.statusText}`,
          data: null
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.',
          data: null
        };
      } else {
        return {
          success: false,
          message: `İletişim eklenirken hata oluştu: ${error.message || 'Bilinmeyen bir hata'}`,
          data: null
        };
      }
    }
  },
  createCustomerCommunication: async (customerCode: string, communicationData: any, token?: string, options?: { requestId?: string }): Promise<ApiResponse<any>> => {
    // Backend'in beklediği isteğe uygun olacak şekilde payload'ı normalize et
    const data: any = { ...communicationData };
    // CommAddress -> Communication dönüştür (controller Communication bekliyor)
    if (!data.Communication && data.CommAddress) {
      data.Communication = data.CommAddress;
      delete data.CommAddress;
    }
    // IsDefault alanını garanti altına al (küçük harfli isDefault gönderildiyse toparla)
    if (data.IsDefault === undefined) {
      const incoming = communicationData.IsDefault ?? communicationData.isDefault;
      data.IsDefault = incoming !== undefined ? Boolean(incoming) : true;
    }
    // Route'ta zaten var, body'den CustomerCode'u çıkar
    if (data.CustomerCode) delete data.CustomerCode;
    
    try {
      devLog('Gönderilen iletişim verisi (normalize):', data);
      
      // Normal müşteri oluşturma akışında token kullanma, axiosInstance kullan
      // QR code akışında ise token kullanılacak
      if (token) {
        // Token ile QR code akışı - doğrudan axios kullan
        devLog('QR Code akışı: İletişim eklemede özel token kullanılıyor');
        
        const headers: any = { 'Content-Type': 'application/json' };
        // Token'ın başında 'Bearer ' var mı kontrol et
        const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        headers['Authorization'] = tokenValue;
        if (options?.requestId) {
          headers['X-Request-Id'] = options.requestId;
        }
        devLog('İletişim API çağrısına eklenen Authorization header:', tokenValue.substring(0, 20) + '...');
        
        // axiosInstance yerine doğrudan axios kullan
        const response = await axios.post<ApiResponse<any>>(
          `${API_BASE_URL}/api/v1/CustomerCommunication/${customerCode}/communications`, 
          data,
          { headers }
        );
        return response.data;
      } else {
        // Normal akış - axiosInstance kullan (admin token'ı ile)
        devLog('Normal akış: İletişim eklemede admin token kullanılıyor');
        const response = await axiosInstance.post<ApiResponse<any>>(
          `/api/v1/CustomerCommunication/${customerCode}/communications`, 
          data,
          options?.requestId ? { headers: { 'X-Request-Id': options.requestId } } : undefined
        );
        return response.data;
      }
    } catch (error: any) {
      console.error('İletişim bilgisi eklerken hata detayı:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        requestData: data
      });
      
      // Hata mesajını daha detaylı göster
      if (error.response?.data) {
        console.error('Backend hata mesajı:', JSON.stringify(error.response.data, null, 2));
      }
      // Hata durumunu işle ve uygun yanıtı döndür
      if (error.response) {
        return {
          success: false,
          message: `Adres eklenirken hata oluştu: ${error.response.status} ${error.response.statusText}`,
          data: null
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.',
          data: null
        };
      } else {
        return {
          success: false,
          message: `Adres eklenirken hata oluştu: ${error.message || 'Bilinmeyen bir hata'}`,
          data: null
        };
      }
    }
  },
  getCustomerCommunications: async (customerCode: string): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`/api/v1/CustomerCommunication/${customerCode}/communications`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'İletişim bilgileri alınamadı');
  },
  getCustomerContacts: async (customerCode: string): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`/api/v1/CustomerContact/${customerCode}/contacts`);
    return response.data.data || [];
  },
  createCustomerContact: async (customerCode: string, contactData: any, token?: string, options?: { requestId?: string }): Promise<ApiResponse<any>> => {
    // FirstName ve LastName yoksa contact kaydı yapmaya gerek yok
    if (!contactData.FirstName || !contactData.LastName) {
      return { success: false, message: "Ad ve soyad bilgileri gereklidir", data: null };
    }
    
    // Backend'in beklediği formata uygun veri yapısı oluştur - sadece gerekli alanlar
    // TitleCode, ContactTypeCode, CreatedUserName, LastUpdatedUserName backend tarafında otomatik doldurulacak
    const data: any = {
      CustomerCode: customerCode,
      FirstName: contactData.FirstName,
      LastName: contactData.LastName
    };
    
    // Opsiyonel alanları sadece değer varsa ekle
    if (contactData.IdentityNum && contactData.IdentityNum.trim() !== "") {
      data.IdentityNum = contactData.IdentityNum;
    }
    
    // IsAuthorized alanı varsa ekle
    if (contactData.IsAuthorized !== undefined) {
      data.IsAuthorized = contactData.IsAuthorized;
    }
    
    try {
      devLog('Gönderilen bağlantılı kişi verisi:', data);
      
      let response;
      
      // Normal müşteri oluşturma akışında token kullanma, axiosInstance kullan
      // QR code akışında ise token kullanılacak
      if (token) {
        // Token ile QR code akışı - doğrudan axios kullan
        devLog('QR Code akışı: Bağlantılı kişi eklemede özel token kullanılıyor');
        
        const headers: any = { 'Content-Type': 'application/json' };
        // Token'ın başında 'Bearer ' var mı kontrol et
        const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        headers['Authorization'] = tokenValue;
        if (options?.requestId) {
          headers['X-Request-Id'] = options.requestId;
        }
        devLog('Bağlantılı kişi API çağrısına eklenen Authorization header:', tokenValue.substring(0, 20) + '...');
        
        // axiosInstance yerine doğrudan axios kullan
        response = await axios.post<ApiResponse<any>>(
          `${API_BASE_URL}/api/v1/CustomerContact/${customerCode}/contacts`, 
          data,
          { headers }
        );
      } else {
        // Normal akış - axiosInstance kullan (admin token'ı ile)
        devLog('Normal akış: Bağlantılı kişi eklemede admin token kullanılıyor');
        response = await axiosInstance.post<ApiResponse<any>>(
          `/api/v1/CustomerContact/${customerCode}/contacts`, 
          data,
          options?.requestId ? { headers: { 'X-Request-Id': options.requestId } } : undefined
        );
      }
      
      // Bağlantılı kişi başarıyla eklendiyse ve iletişim bilgileri varsa, iletişim bilgilerini ekle
      if (response.data.success) {
        // Telefon ve/veya e-posta bilgisi varsa ekle
        const communicationPromises = [];
        
        if (contactData.Phone && contactData.Phone.trim() !== "") {
          const phoneData = {
            CustomerCode: customerCode,
            CommunicationTypeCode: "1", // 1: Telefon
            Communication: contactData.Phone,
            IsDefault: !contactData.Email // Eğer e-posta yoksa telefonu varsayılan yap
          };
          
          // Token varsa QR code akışı, yoksa normal akış
          if (token) {
            // QR code akışı - doğrudan axios kullan
            const phoneHeaders: any = { 'Content-Type': 'application/json' };
            // Token'ın başında 'Bearer ' var mı kontrol et
            const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
            phoneHeaders['Authorization'] = tokenValue;
            if (options?.requestId) {
              phoneHeaders['X-Request-Id'] = options.requestId;
            }
            
            communicationPromises.push(
              axios.post<ApiResponse<any>>(
                `${API_BASE_URL}/api/v1/CustomerCommunication/${customerCode}/communications`, 
                phoneData,
                { headers: phoneHeaders }
              ).catch(err => console.error('Telefon bilgisi eklenirken hata:', err))
            );
          } else {
            // Normal akış - axiosInstance kullan
            communicationPromises.push(
              axiosInstance.post<ApiResponse<any>>(
                `/api/v1/CustomerCommunication/${customerCode}/communications`, 
                phoneData,
                options?.requestId ? { headers: { 'X-Request-Id': options.requestId } } : undefined
              ).catch(err => console.error('Telefon bilgisi eklenirken hata:', err))
            );
          }
        }
        
        if (contactData.Email && contactData.Email.trim() !== "") {
          const emailData = {
            CustomerCode: customerCode,
            CommunicationTypeCode: "3", // 3: E-posta
            Communication: contactData.Email,
            IsDefault: !contactData.Phone // Eğer telefon yoksa e-postayı varsayılan yap
          };
          
          // Token varsa QR code akışı, yoksa normal akış
          if (token) {
            // QR code akışı - doğrudan axios kullan
            const emailHeaders: any = { 'Content-Type': 'application/json' };
            // Token'ın başında 'Bearer ' var mı kontrol et
            const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
            emailHeaders['Authorization'] = tokenValue;
            if (options?.requestId) {
              emailHeaders['X-Request-Id'] = options.requestId;
            }
            
            communicationPromises.push(
              axios.post<ApiResponse<any>>(
                `${API_BASE_URL}/api/v1/CustomerCommunication/${customerCode}/communications`, 
                emailData,
                { headers: emailHeaders }
              ).catch(err => console.error('E-posta bilgisi eklenirken hata:', err))
            );
          } else {
            // Normal akış - axiosInstance kullan
            communicationPromises.push(
              axiosInstance.post<ApiResponse<any>>(
                `/api/v1/CustomerCommunication/${customerCode}/communications`, 
                emailData,
                options?.requestId ? { headers: { 'X-Request-Id': options.requestId } } : undefined
              ).catch(err => console.error('E-posta bilgisi eklenirken hata:', err))
            );
          }
        }
        
        if (communicationPromises.length > 0) {
          try {
            await Promise.all(communicationPromises);
            devLog('Bağlantılı kişi iletişim bilgileri eklendi');
          } catch (commError) {
            console.error('Bazı iletişim bilgileri eklenirken hata oluştu:', commError);
          }
        }
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Bağlantılı kişi eklerken hata detayı:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        requestData: data
      });
      
      // Hata mesajını daha detaylı göster
      if (error.response?.data) {
        console.error('Backend hata mesajı:', JSON.stringify(error.response.data, null, 2));
      }
      
      // Kullanıcı dostu hata mesajı oluştur
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          "Bağlantılı kişi eklenirken bir hata oluştu";
      
      return {
        success: false,
        message: errorMessage,
        data: null
      };
    }
  },
  getCountries: async (langCode: string = 'TR'): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/api/v1/Location/countries', { params: { langCode } });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Ülkeler alınamadı');
  },
  getCustomerTypes: async (): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/api/v1/CustomerBasic/customer-types');
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Müşteri tipleri alınamadı');
  },
  getLocationHierarchy: async (langCode: string = 'TR', countryCode: string = 'TR'): Promise<any> => {
    const response = await axiosInstance.get<ApiResponse<any>>('/api/v1/Location/hierarchy', { params: { langCode, countryCode } });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Lokasyon hiyerarşisi alınamadı');
  },
  getRegions: async (langCode: string = 'TR'): Promise<any[]> => { // State/Bölge için
    const response = await axiosInstance.get<ApiResponse<any[]>>('/api/v1/Location/states', { params: { langCode } });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Bölgeler alınamadı');
  },
  getCitiesByRegion: async (regionCode: string, langCode: string = 'TR'): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`/api/v1/CustomerLocation/states/${regionCode}/cities`, { params: { langCode } }); // Endpoint'i kontrol edin, CustomerLocation veya Location olabilir
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Şehirler alınamadı');
  },
  getDistrictsByCity: async (cityCode: string, langCode: string = 'TR'): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`/api/v1/CustomerLocation/cities/${cityCode}/districts`, { params: { langCode } }); // Endpoint'i kontrol edin
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'İlçeler alınamadı');
  },
  getTaxOffices: async (): Promise<TaxOfficeResponse[]> => {
    try {
      devLog('API: Fetching tax offices from backend');
      const response = await axiosInstance.get<ApiResponse<TaxOfficeResponse[]>>('/api/v1/Warehouse/tax-offices');
      
      if (response.data.success && response.data.data) {
        devLog(`API: Successfully fetched ${response.data.data.length} tax offices`);
        return response.data.data;
      } else {
        devWarn('API: Tax offices endpoint returned success=false or no data', response.data);
        return [];
      }
    } catch (error) {
      console.error('API: Error fetching tax offices', error);
      // Kullanıcının isteği doğrultusunda hata durumunda boş dizi döndürüyoruz, mock veri kullanmıyoruz
      return [];
    }
  },
  
  // Token ile vergi dairelerini getiren fonksiyon
  getTaxOfficesWithToken: async (token: string): Promise<TaxOfficeResponse[]> => {
    try {
      devLog('API: Fetching tax offices with token');
      // Token'ı URL parametresi olarak ekleyerek istek yapıyoruz
      const response = await axios.get<ApiResponse<TaxOfficeResponse[]>>(`${API_BASE_URL}/api/v1/Warehouse/tax-offices-with-token`, {
        params: { token }
      });
      
      if (response.data.success && response.data.data) {
        devLog(`API: Successfully fetched ${response.data.data.length} tax offices with token`);
        return response.data.data;
      } else {
        devWarn('API: Tax offices endpoint with token returned success=false or no data', response.data);
        return [];
      }
    } catch (error) {
      console.error('API: Error fetching tax offices with token', error);
      return [];
    }
  },
  getTaxOfficesByCity: async (cityCode: string, langCode: string = 'TR'): Promise<TaxOfficeResponse[]> => {
     devLog(`API: Fetching tax offices for city: ${cityCode}`);
     // Bu endpoint backend'de olmayabilir, CustomerBasic/tax-offices/{cityCode} gibi bir yapı gerekebilir
     // Şimdilik tümünü çekip filtrelemeyi deneyebiliriz veya backend'de endpoint oluşturulmalı
     // const response = await axiosInstance.get<ApiResponse<TaxOfficeResponse[]>>(`/api/v1/CustomerBasic/tax-offices/${cityCode}`, { params: { langCode } });
     // Geçici olarak tümünü çekip filtreleyelim (performans sorunlarına yol açabilir)
     const allTaxOfficesResponse = await customerApi.getTaxOffices(); // Parametresiz çağrı
     const filtered = allTaxOfficesResponse.filter(office => office.cityCode === cityCode); // cityCode alanı TaxOfficeResponse tipinde olmalı
     devLog(`API: Tax offices for city ${cityCode} (filtered):`, filtered);
     return filtered;
  },
  getAddressTypes: async (langCode: string = 'TR'): Promise<AddressTypeResponse[]> => {
    const response = await axiosInstance.get<ApiResponse<AddressTypeResponse[]>>('/api/v1/CustomerAddress/address-types', { params: { langCode }});
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Adres tipleri alınamadı');
  },
  getAddressTypeByCode: async (code: string, langCode: string = 'TR'): Promise<AddressTypeResponse> => {
    const response = await axiosInstance.get<ApiResponse<AddressTypeResponse>>(`/api/v1/CustomerAddress/address-types/${code}`, { params: { langCode }});
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Adres tipi alınamadı');
  },
  getBankAccounts: async (langCode: string = 'TR'): Promise<any[]> => {
    const response = await axiosInstance.get<ApiResponse<any[]>>('/api/v1/CustomerLocation/bank-accounts', { params: { langCode } });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Banka hesapları alınamadı');
  },

  // En son oluşturulan müşterileri (veritabanı bazında) getirir
  // Backend endpoint: /api/v1/customers/son-kayitlar (CustomersLatestController)
  getLatestCustomersPerDb: async (): Promise<any[]> => {
    try {
      const response = await axiosInstance.get('/api/v1/customers/son-kayitlar');
      // Bu endpoint ApiResponse sarmalayıcı olmadan dizi döndürüyor (QueryAsync doğrudan liste)
      const data = Array.isArray(response.data?.data) ? response.data.data : response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('API: En son müşteri kayıtları alınamadı', error);
      return [];
    }
  },

  // TOKEN BAZLI FONKSİYONLAR - CustomerRegister için
  createCustomerWithToken: async (token: string, inputCustomerData: any): Promise<ApiResponse<CustomerCreateResponseNew>> => {
    try {
      // Öncelikle zorunlu alanları kontrol et
      if (!inputCustomerData.CustomerName && !inputCustomerData.customerName) {
        console.error('CustomerName alanı boş olamaz');
        return {
          success: false,
          message: 'Müşteri adı boş olamaz.',
          data: null
        };
      }

      // Backend'in beklediği CustomerData formatına uygun veri hazırla
      // Önemli: Backend'deki CustomerData sınıfı alan isimleri büyük harfle başlıyor (PascalCase)
      const customerData: any = {
        // Temel müşteri bilgileri
        CustomerCode: inputCustomerData.CustomerCode || inputCustomerData.customerCode || "",
        CustomerName: inputCustomerData.CustomerName || inputCustomerData.customerName || "Müşteri",
        CustomerSurname: inputCustomerData.CustomerSurname || inputCustomerData.customerSurname || "",
        TitleCode: inputCustomerData.TitleCode || inputCustomerData.titleCode || "",
        Patronym: inputCustomerData.Patronym || inputCustomerData.patronym || "",
        
        // Vergi ve kimlik bilgileri
        TaxNumber: inputCustomerData.TaxNumber || inputCustomerData.taxNumber || "",
        CustomerIdentityNumber: inputCustomerData.CustomerIdentityNumber || inputCustomerData.identityNumber || "22222222222",
        IdentityNum: inputCustomerData.IdentityNum || inputCustomerData.identityNumber || "22222222222",
        MersisNum: inputCustomerData.MersisNum || inputCustomerData.mersisNum || "",
        TaxOfficeCode: inputCustomerData.TaxOfficeCode || inputCustomerData.taxOffice || "034104",
        
        // Müşteri tipi ve kategori bilgileri
        CustomerTypeCode: String(inputCustomerData.CustomerTypeCode || inputCustomerData.customerTypeCode || "3"),
        IsIndividual: inputCustomerData.IsIndividual !== undefined ? inputCustomerData.IsIndividual : 
                      inputCustomerData.isIndividual !== undefined ? inputCustomerData.isIndividual : false,
        DiscountGroupCode: inputCustomerData.DiscountGroupCode || inputCustomerData.discountGroupCode || "",
        CustomerMarkupGrCode: inputCustomerData.CustomerMarkupGrCode || inputCustomerData.customerMarkupGrCode || "",
        CustomerPaymentPlanGrCode: inputCustomerData.CustomerPaymentPlanGrCode || inputCustomerData.customerPaymentPlanGrCode || "",
        VendorPaymentPlanGrCode: inputCustomerData.VendorPaymentPlanGrCode || inputCustomerData.vendorPaymentPlanGrCode || "",
        
        // Şirket ve ofis bilgileri
        CurrencyCode: inputCustomerData.CurrencyCode || inputCustomerData.currencyCode || "TRY",
        OfficeCode: inputCustomerData.OfficeCode || inputCustomerData.officeCode || "M",
        CompanyCode: String(inputCustomerData.CompanyCode || inputCustomerData.companyCode || "1"),
        SalesmanCode: inputCustomerData.SalesmanCode || inputCustomerData.salesmanCode || "",
        
        // Limit bilgileri
        CreditLimit: inputCustomerData.CreditLimit || inputCustomerData.creditLimit || 0,
        RiskLimit: inputCustomerData.RiskLimit || inputCustomerData.riskLimit || 0,
        
        // Adres bilgileri
        CityCode: inputCustomerData.CityCode || inputCustomerData.cityCode || "34",
        DistrictCode: inputCustomerData.DistrictCode || inputCustomerData.districtCode || "34-1",
        Address: inputCustomerData.Address || inputCustomerData.address || "Osmanbey 1",
        
        // Diğer bilgiler
        DataLanguageCode: inputCustomerData.DataLanguageCode || inputCustomerData.dataLanguageCode || "TR",
        PaymentTerm: inputCustomerData.PaymentTerm || inputCustomerData.paymentTerm || 0
      };
      
      // E-Fatura ve E-İrsaliye başlangıç tarihlerini ayarla
      if (inputCustomerData.isSubjectToEInvoice || inputCustomerData.IsSubjectToEInvoice) {
        customerData.EInvoiceStartDate = new Date().toISOString();
      }
      
      if (inputCustomerData.isSubjectToEDispatch || inputCustomerData.IsSubjectToEDispatch) {
        customerData.EShipmentStartDate = new Date().toISOString();
      }
      
      // Backend'in beklediği formata uygun request body oluştur
      const requestBody = {
        Token: token,
        CustomerData: customerData
      };
      
      devLog('Gönderilecek istek gövdesi:', requestBody);

      // Backend API'sine token ile istek gönder
      const response = await axios.post<ApiResponse<CustomerCreateResponseNew>>(
        `${API_BASE_URL}/api/v1/Customer/CreateCustomerWithToken`, 
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Başarılı yanıtı döndür
      devLog('API başarılı yanıt:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('API: Customer create with token error:', error);
      
      // Detaylı hata bilgilerini logla
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // HTTP yanıtı alındı ancak 2xx başarılı durum kodu dışında
          console.error('API error response details:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          });
          
          // Tam yanıt verilerini logla
          if (error.response.data) {
            console.error('API error response data:', JSON.stringify(error.response.data, null, 2));
          }
          
          // HTTP durum koduna göre özel hata mesajları
          if (error.response.status === 400) {
            // Backend'den gelen validasyon hatalarını parse et
            const errorData = error.response.data;
            let detailedMessage = 'Geçersiz istek formatı veya eksik/hatalı veri.';
            
            if (errorData && typeof errorData === 'object') {
              if (errorData.message) {
                detailedMessage = errorData.message;
              } else if (errorData.Message) {
                detailedMessage = errorData.Message;
              } else if (errorData.error) {
                detailedMessage = errorData.error;
              } else if (errorData.Error) {
                detailedMessage = errorData.Error;
              } else if (errorData.errors) {
                // Model doğrulama hatalarını kontrol et
                const validationErrors = errorData.errors;
                console.error('API validation errors:', validationErrors);
                
                // Model doğrulama hatalarını birleştir
                const errorMessages = Object.values(validationErrors).flat();
                if (errorMessages.length > 0) {
                  detailedMessage = errorMessages.join(', ');
                }
              }
            }
            
            return {
              success: false,
              message: detailedMessage,
              data: null
            };
          } else if (error.response.status === 401) {
            return {
              success: false,
              message: 'Yetkilendirme hatası. Token geçersiz veya süresi dolmuş.',
              data: null
            };
          } else if (error.response.status === 500) {
            return {
              success: false,
              message: 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.',
              data: null
            };
          }
        }
      }
      
      return {
        success: false,
        message: 'Müşteri oluşturulurken bir hata oluştu.',
        data: null
      };
    }
  },

  // Müşteri adres bilgilerini eklemek için API çağrısı
  addCustomerAddress: async (customerId: string, addressData: any): Promise<ApiResponse<any>> => {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/api/v1/Customer/${customerId}/addresses`,
        addressData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error: unknown) {
      console.error('API: Add customer address error:', error);
      
      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          message: `Adres eklenirken hata oluştu: ${error.response.data?.message || error.message}`,
          data: null
        };
      }
      
      return {
        success: false,
        message: "Adres eklenirken bir hata oluştu",
        data: null
      };
    }
  },

  // API hatalarını işlemek için yardımcı fonksiyon
  handleApiError: (error: any): ApiResponse<any> => {
    if (error.response) {
      // Sunucu yanıt döndü ama hata kodu ile
      console.error('HTTP error:', error.response.status, error.response.statusText);
      return {
        success: false,
        message: `HTTP Hatası: ${error.response.status} ${error.response.statusText}`,
        data: null
      };
    } else if (error.request) {
      // İstek gönderildi ancak yanıt alınamadı
      console.error('No response received:', error.request);
      return {
        success: false,
        message: 'Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.',
        data: null
      };
    } else {
      // İstek oluşturulurken bir hata oluştu
      return {
        success: false,
        message: `İstek hatası: ${error.message || 'Bilinmeyen bir hata oluştu'}`,
        data: null
      };
    }
  },

  createCustomerAddressWithToken: async (token: string, customerCode: string, addressData: any): Promise<ApiResponse<any>> => {
    const data = {
      ...addressData,
      isDefault: addressData.isDefault !== undefined ? addressData.isDefault : true
    };
    
    try {
      devLog('Token ile gönderilen adres verisi:', data);
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/api/v1/CustomerAddress/${customerCode}/addresses-with-token`, 
        data,
        {
          params: { token }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Token ile adres eklerken hata detayı:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        requestData: data
      });
      
      if (error.response?.data) {
        console.error('Backend hata mesajı:', JSON.stringify(error.response.data, null, 2));
      }
      // Hata durumunu işle ve uygun yanıtı döndür
      if (error.response) {
        return {
          success: false,
          message: `Adres eklenirken hata oluştu: ${error.response.status} ${error.response.statusText}`,
          data: null
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.',
          data: null
        };
      } else {
        return {
          success: false,
          message: `Adres eklenirken hata oluştu: ${error.message || 'Bilinmeyen bir hata'}`,
          data: null
        };
      }
    }
  },

  createCustomerCommunicationWithToken: async (token: string, customerCode: string, communicationData: any): Promise<ApiResponse<any>> => {
    const data = {
      ...communicationData,
      isDefault: communicationData.isDefault !== undefined ? communicationData.isDefault : true
    };
    
    try {
      devLog('Token ile gönderilen iletişim verisi:', data);
      const response = await axios.post<ApiResponse<any>>(
        `${API_BASE_URL}/api/v1/CustomerCommunication/${customerCode}/communications-with-token`, 
        data,
        {
          params: { token }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Token ile iletişim bilgisi eklerken hata detayı:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        requestData: data
      });
      
      if (error.response?.data) {
        console.error('Backend hata mesajı:', JSON.stringify(error.response.data, null, 2));
      }
      // Hata durumunu işle ve uygun yanıtı döndür
      if (error.response) {
        return {
          success: false,
          message: `İletişim bilgisi eklenirken hata oluştu: ${error.response.status} ${error.response.statusText}`,
          data: null
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.',
          data: null
        };
      } else {
        return {
          success: false,
          message: `İletişim bilgisi eklenirken hata oluştu: ${error.message || 'Bilinmeyen bir hata'}`,
          data: null
        };
      }
    }
  },

  getCountriesWithToken: async (token: string, langCode: string = 'TR'): Promise<any[]> => {
    try {
      const response = await axios.get<ApiResponse<any[]>>(`${API_BASE_URL}/api/v1/Location/countries-with-token`, { 
        params: { token, langCode } 
      });
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Ülkeler alınamadı');
    } catch (error) {
      console.error('Token ile ülkeler alınırken hata:', error);
      return [{ countryCode: 'TR', countryDescription: 'Türkiye' }];
    }
  },

  getCustomerTypesWithToken: async (token: string): Promise<any[]> => {
    try {
      const response = await axios.get<ApiResponse<any[]>>(`${API_BASE_URL}/api/v1/CustomerBasic/customer-types-with-token`, {
        params: { token }
      });
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Müşteri tipleri alınamadı');
    } catch (error) {
      console.error('Token ile müşteri tipleri alınırken hata:', error);
      return [];
    }
  },

  getLocationHierarchyWithToken: async (token: string, langCode: string = 'TR', countryCode: string = 'TR'): Promise<any> => {
    try {
      const response = await axios.get<ApiResponse<any>>(`${API_BASE_URL}/api/v1/Location/hierarchy-with-token`, { 
        params: { token, langCode, countryCode } 
      });
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Lokasyon hiyerarşisi alınamadı');
    } catch (error) {
      console.error('Token ile lokasyon hiyerarşisi alınırken hata:', error);
      return null;
    }
  }
};

export const currencyApi = {
  getCurrencies: async (langCode: string = 'TR'): Promise<CurrencyResponse[]> => {
    console.log('API: Fetching currencies with langCode:', langCode);
    try {
      // API yanıtı doğrudan dizi olarak dönüyor, ApiResponse yapısında değil
      const response = await axiosInstance.get<CurrencyResponse[]>('/api/v1/Currency', { params: { langCode } });
      if (response.data && Array.isArray(response.data)) {
        console.log(`API: Successfully fetched ${response.data.length} currencies`);
        return response.data;
      }
      console.warn('API: Currency endpoint returned unexpected format', response.data);
      return [];
    } catch (error) {
      console.error('API: Exception while fetching currencies', error);
      // Kullanıcının isteği doğrultusunda hata durumunda boş dizi döndürüyoruz, mock veri kullanmıyoruz
      return [];
    }
  },
  
  // Token ile para birimlerini getiren fonksiyon
  getCurrenciesWithToken: async (token: string, langCode: string = 'TR'): Promise<CurrencyResponse[]> => {
    console.log('API: Fetching currencies with token and langCode:', langCode);
    try {
      const response = await axios.get<ApiResponse<CurrencyResponse[]>>(`${API_BASE_URL}/api/v1/Currency/currencies-with-token`, {
        params: { token, langCode }
      });
      
      if (response.data.success && response.data.data) {
        console.log(`API: Successfully fetched ${response.data.data.length} currencies with token`);
        return response.data.data;
      } else {
        console.warn('API: Currencies endpoint with token returned success=false or no data', response.data);
        return [];
      }
    } catch (error) {
      console.error('API: Error fetching currencies with token', error);
      return [];
    }
  }
};

// Eski auth API tanımlaması - artık kullanılmıyor
export const legacyAuthApi = {
  login: async (credentials: any) => {
    const response = await axiosInstance.post('/api/v1/auth/login', credentials);
    return response.data; // Genellikle { token: '...', user: {...} } gibi bir yapı döner
  },
  logout: async () => {
    return await axiosInstance.post('/api/v1/auth/logout');
  },
  getCurrentUser: async () => {
    console.log('👤 API: getCurrentUser çağrıldı');
    const response = await axiosInstance.get('/api/User/current'); // veya /api/v1/Auth/current-user
    console.log('✅ API: Kullanıcı verisi başarıyla alındı');
    return response.data;
  },
  changePassword: async (data: any) => {
    return await axiosInstance.post('/api/v1/auth/change-password', data);
  },
  forgotPassword: async (data: any) => {
    return await axiosInstance.post('/api/v1/auth/forgot-password', data);
  },
  resetPassword: async (data: any) => {
    return await axiosInstance.post('/api/v1/auth/reset-password', data);
  },
  getUserLoginLogs: async () => { // Örnek bir endpoint, sizinkine göre ayarlayın
    const response = await axiosInstance.get('/api/v1/auth/login-logs'); 
    return response.data;
  }
};

// Diğer API grupları (invoiceApi, productApi vb.) burada olabilir

// Sevkiyat yöntemleri API'si
export const shipmentApi = {
  getShipmentMethods: async () => {
    try {
      console.log('API: Fetching shipment methods');
      const response = await axiosInstance.get<ApiResponse<ShipmentMethodResponse[]>>('/api/v1/ShipmentMethod');
      if (response.data.success) {
        console.log('API: Shipment methods fetched successfully', response.data.data);
        return response.data.data;
      }
      console.error('API: Error fetching shipment methods', response.data.message);
      throw new Error(response.data.message || 'Sevkiyat yöntemleri alınamadı');
    } catch (error) {
      console.error('API: Exception while fetching shipment methods', error);
      throw error;
    }
  }
};

export const invoiceApi = {
  // Örnek fatura API çağrıları
  getInvoices: async (params: any) => {
    const response = await axiosInstance.get('/api/v1/SalesInvoice', { params });
    return response.data; 
  },
  getInvoiceById: async (id: string) => {
    const response = await axiosInstance.get(`/api/v1/SalesInvoice/${id}`);
    return response.data;
  },
  createInvoice: async (data: any) => {
    const response = await axiosInstance.post('/api/v1/SalesInvoice', data);
    return response.data;
  },
  cancelInvoice: async (id: string) => {
    const response = await axiosInstance.patch(`/api/v1/SalesInvoice/${id}/cancel`);
    return response.data;
  },
  getPaymentPlans: async (forCreditCardPlan?: boolean, isBlocked?: boolean) => {
    const response = await axiosInstance.get('/api/v1/SalesInvoice/payment-plans', { params: { forCreditCardPlan, isBlocked }});
    return response.data;
  },
  getOffices: async (isBlocked?: boolean) => {
    const response = await axiosInstance.get('/api/v1/SalesInvoice/offices', { params: { isBlocked }});
    return response.data;
  },
  getWholesaleInvoices: async (params: any) => {
    const response = await axiosInstance.get('/api/v1/Invoice/wholesale', { params });
    return response.data;
  },
  getPurchaseInvoices: async (params: any) => {
    const response = await axiosInstance.get('/api/v1/Invoice/purchase', { params });
    return response.data;
  },
   getSalesInvoices: async (params: any) => {
    const response = await axiosInstance.get('/api/v1/Invoice/sales', { params }); // SalesInvoiceController'a göre düzenlenebilir
    return response.data;
  },
  getWholesalePurchaseInvoices: async (params: any) => {
    const response = await axiosInstance.get('/api/v1/Invoice/wholesale-purchase', { params });
    return response.data;
  },
  createWholesaleInvoice: async (data: any) => {
    const response = await axiosInstance.post('/api/v1/Invoice', data); // Genel InvoiceController POST endpoint'i
    return response.data;
  },
  getInvoicePdf: async (invoiceHeaderID: string): Promise<Blob> => {
    const response = await axiosInstance.get(`/api/v1/Invoice/${invoiceHeaderID}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
  sendEInvoice: async (invoiceHeaderID: string): Promise<any> => {
    const response = await axiosInstance.post(`/api/v1/Invoice/${invoiceHeaderID}/send-einvoice`);
    return response.data;
  },
   downloadInvoicePdf: async (invoiceId: string): Promise<Blob> => {
    const response = await axiosInstance.get(`/api/v1/Invoice/download/${invoiceId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};


export const cashApi = {
  getAllCashTransactions: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/payment/cash-transactions', { params: { langCode } });
    return response.data.data; 
  },
  getCashTransactionsByCurrency: async (currencyCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/payment/cash-transactions', { 
      params: { 
        currencyCode,
        langCode 
      }
    });
    return response.data.data;
  },
  getCashTransactionsByType: async (cashTransTypeCode: number, langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/payment/cash-transactions', { 
      params: { 
        transactionType: cashTransTypeCode,
        langCode 
      }
    });
    return response.data.data;
  },
  getCashTransactionsByCurrencyAndType: async (currencyCode: string, cashTransTypeCode: number, langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/payment/cash-transactions', { 
      params: { 
        currencyCode,
        transactionType: cashTransTypeCode,
        langCode 
      }
    });
    return response.data.data;
  },
  getCashTransactionsByDateRange: async (startDate: Date, endDate: Date, langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/payment/cash-transactions', { 
      params: { 
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        langCode 
      }
    });
    return response.data.data;
  },
  getCashTransactionDetail: async (transactionNumber: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/payment/cash-transaction/${transactionNumber}`, { 
      params: { langCode }
    });
    return response.data.data;
  }
};

export const customerCreditApi = {
  getAllCustomerCredits: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerCredit/all', { params: { langCode } });
    return response.data.data;
  },
  getCustomerCreditsByCustomerCode: async (customerCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerCredit/customer/${customerCode}`, { params: { langCode } });
    return response.data.data;
  },
  getCustomerCreditsByCurrency: async (currencyCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerCredit/currency/${currencyCode}`, { params: { langCode } });
    return response.data.data;
  },
  getCustomerCreditsByPaymentType: async (paymentTypeCode: number, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerCredit/payment-type/${paymentTypeCode}`, { params: { langCode } });
    return response.data.data;
  },
  getOverdueCustomerCredits: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerCredit/overdue', { params: { langCode } });
    return response.data.data;
  },
  getCustomerCreditSummary: async (customerCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerCredit/summary/customer/${customerCode}`, { params: { langCode } });
    return response.data.data;
  },
  getAllCustomerCreditSummaries: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerCredit/summary/all', { params: { langCode } });
    return response.data.data;
  }
};

export const customerDebtApi = {
  getAllCustomerDebts: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerDebt/all', { params: { langCode } });
    return response.data.data;
  },
  getCustomerDebtsByCustomerCode: async (customerCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerDebt/customer/${customerCode}`, { params: { langCode } });
    return response.data.data;
  },
  getCustomerDebtsByCurrency: async (currencyCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerDebt/currency/${currencyCode}`, { params: { langCode } });
    return response.data.data;
  },
  getOverdueCustomerDebts: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerDebt/overdue', { params: { langCode } });
    return response.data.data;
  },
  getCustomerDebtSummary: async (customerCode: string, langCode: string = 'TR') => {
    const response = await axiosInstance.get(`/api/v1/CustomerDebt/summary/customer/${customerCode}`, { params: { langCode } });
    return response.data.data;
  },
  getAllCustomerDebtSummaries: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerDebt/summary/all', { params: { langCode } });
    return response.data.data;
  }
};

export const bankApi = {
  getBankTransactionReport: async (params: any) => {
    const response = await axiosInstance.post('/api/v1/bank/transactions', params);
    return response.data;
  },
  getBankSummary: async (bankAccountCode: string) => {
    const response = await axiosInstance.get('/api/v1/bank/summary', { 
      params: { 
        bankAccountCode
      }
    });
    return response.data;
  },
  getBankAccounts: async (langCode: string = 'TR') => {
    const response = await axiosInstance.get('/api/v1/CustomerLocation/bank-accounts', { params: { langCode } });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Banka hesapları alınamadı');
  }
};

// Auth API'yi doğrudan import/export ediyoruz
export { authApi } from './authApi';
export * from './roleApi';
export * from './userApi';

// Diğer API exportları...

// Default export ekliyoruz, böylece diğer servisler import edebilir
export default {
  ...api,
  customerApi,
  customerDebtApi,
  vendorApi
};
